import {Action, ComplaintReducer, ComplaintState} from "../types";
import {getDefaultComplaint} from "../AppStateDefaults";

type RootAction =
    { type: 'CANCEL_COMPLAINT' } |
    { type: 'CREATE_SECONDARY_COMPLAINT', payload: { usePreviousPatientAndTreatmentDates: boolean } }

export const cancelComplaint = (): RootAction => ({type: 'CANCEL_COMPLAINT'})
export const createSecondaryComplaint = (usePreviousPatientAndTreatmentDates: boolean): RootAction => ({
    type: 'CREATE_SECONDARY_COMPLAINT',
    payload: {usePreviousPatientAndTreatmentDates}
})

const RootReducerFactory = (complaintReducer: ComplaintReducer) => (state: ComplaintState, action: Action) => {
    switch (action.type) {
        case 'CANCEL_COMPLAINT':
            return complaintReducer(getDefaultComplaint(), action)
        case 'CREATE_SECONDARY_COMPLAINT':
            const initialState = getDefaultComplaint()
            // Complaint with Customer Data
            const cwc = true
            // Complaint on Secondary Device
            const csd = state.secondaryDevice.hasSecondaryDeviceComplaint === 'YES'
            // Complaint with Patient and Treatment Data
            const cpt = action.payload.usePreviousPatientAndTreatmentDates

            return complaintReducer({
                ...initialState,
                metadata: {
                    isInitialComplaint: false,
                    complaintWithCustomerData: cwc,
                    complaintOnSecondaryDevice: csd,
                    complaintWithPatientAndTreatmentData: cpt
                },
                guestInfo: cwc ? {...state.guestInfo} : {...initialState.guestInfo},
                generalInfo: {
                    ...initialState.generalInfo,
                    contactName: state.generalInfo.contactName,
                    isAcknowledged: state.generalInfo.isAcknowledged
                },
                generalComplaint: csd
                    ? {
                        selectedProduct: state.secondaryDevice.selectedProduct,
                        isProductUnknown: state.secondaryDevice.isProductUnknown as boolean,
                        isBatchOrLotNumberKnown: state.secondaryDevice.batchOrLotNumber ? 'YES' : 'NO',
                        batchOrLotNumber: state.secondaryDevice.batchOrLotNumber,
                        wasProductPlaced: cpt ? state.generalComplaint.wasProductPlaced : initialState.generalComplaint.wasProductPlaced,
                        wasPatientInvolved: '',
                        productType: '',
                        wasOpenedOrDropped: '',
                        wasAnotherImplantPlaced: cpt ? state.generalComplaint.wasAnotherImplantPlaced : initialState.generalComplaint.wasAnotherImplantPlaced,
                    }
                    : {
                        ...initialState.generalComplaint,
                        wasProductPlaced: cpt ? state.generalComplaint.wasProductPlaced : initialState.generalComplaint.wasProductPlaced,
                        wasAnotherImplantPlaced: cpt ? state.generalComplaint.wasAnotherImplantPlaced : initialState.generalComplaint.wasAnotherImplantPlaced,
                    },
                complaintType: csd
                    ? {
                        ...state.complaintType,
                        // Keep dateOfOccurrence and location from primary complaint
                        // Reset only complaint-specific fields
                        complaintType: '',
                        otherExplanation: '',
                        surfaceDefectExplanation: '',
                        estheticExplanation: '',
                        // Reset attachmentIds since resetIndexedDb() clears the database
                        attachmentIds: []
                    }
                    : {...initialState.complaintType},
                treatmentDates: csd || cpt
                    ? {
                        ...state.treatmentDates,
                        // Reset all dates for secondary complaints and "use same patient details"
                        dateOfTreatment: null,
                        dateOfImplantLoading: null,
                        dateOfSecondSurgery: null,
                        dateProductWasRemoved: null,
                        // Also reset radio selections so user must re-answer
                        wasImplantLoaded: '',
                        wasThereASecondSurgery: '',
                        wasProductRemoved: '',
                    }
                    : initialState.treatmentDates,
                patientInfo: csd || cpt ? {...state.patientInfo} : initialState.patientInfo,
                clinicalCondition: {...initialState.clinicalCondition},
                abutmentsInfo: state.generalComplaint.productType === 'ABUTMENT' && csd && !state.generalComplaint.selectedProduct?.isDiscontinuedOrUnknown
                    ? {...initialState.abutmentsInfo, implantBrandUsed: 'ORIGINAL_NOBEL_BIOCARE'}
                    : {...initialState.abutmentsInfo},
                secondaryDevice: csd
                    ? {
                        ...initialState.secondaryDevice,
                        hasSecondaryDevice: 'NO',
                    }
                    : {...initialState.secondaryDevice},
                shippingInfo: {...state.shippingInfo}
            }, action)
        default:
            return complaintReducer(state, action)
    }
}

export default RootReducerFactory
